import { ROLE_LABELS } from '@whn/shared';
import { ArrowRight, Building2, Contact, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/page-header';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth, useCurrentUser } from '@/features/auth/auth-context';
import { MyDayPanel } from './my-day-panel';
import { PipelineSummaryCard } from './pipeline-summary-card';

/**
 * The working dashboard: what needs doing today on the left, pipeline health
 * on the right. Both read live data — this is the screen a rep opens first,
 * so it answers "what now?" rather than describing the product.
 */
export function DashboardPage() {
  const user = useCurrentUser();
  const { can } = useAuth();

  return (
    <>
      <PageHeader
        title={`Welcome, ${user.firstName}`}
        description={`You are signed in to ${user.organization.name} as ${ROLE_LABELS[user.role]}.`}
      />

      <div className="space-y-6 p-6">
        <div className="grid gap-6 lg:grid-cols-2">
          {can('activity:read') ? <MyDayPanel /> : null}
          {can('deal:read') ? <PipelineSummaryCard /> : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <QuickLink
            to="/leads"
            icon={Sparkles}
            title="Leads"
            description="Work your prospects and convert the qualified ones."
          />
          <QuickLink
            to="/accounts"
            icon={Building2}
            title="Accounts"
            description="The companies you sell to, and their group structure."
          />
          <QuickLink
            to="/contacts"
            icon={Contact}
            title="Contacts"
            description="The people behind every deal."
          />
        </div>
      </div>
    </>
  );
}

function QuickLink({
  to,
  icon: Icon,
  title,
  description,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <Card className="transition-colors hover:border-brand/40">
      <CardContent className="pt-5">
        <Link to={to} className="group block space-y-1.5">
          <span className="flex items-center gap-2 font-medium text-ink">
            <Icon className="size-4 text-ink-muted" aria-hidden="true" />
            {title}
            <ArrowRight
              className="size-4 opacity-0 transition-opacity group-hover:opacity-100"
              aria-hidden="true"
            />
          </span>
          <span className="block text-sm text-ink-muted">{description}</span>
        </Link>
      </CardContent>
    </Card>
  );
}
